import { useEffect, useState } from 'react';
// material
import { Link, Stack, Checkbox, TextField, IconButton, InputAdornment, FormControlLabel } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// component
import { baseURL } from '../backend_url';
import axios from 'axios';
import Swal from 'sweetalert2';
import Iconify from './Iconify';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  LOGIN_VERIFICATION_METHODS,
  buildLoginChallengeRequest,
  buildLoginVerificationRequest,
  formatEmailOtpTimeRemaining,
  getEmailOtpSecondsRemaining,
  isEmailOtpExpired,
  isLoginVerificationMatched,
  parseLoginVerificationChallenge,
  parsePasswordResetChallenge,
} from './loginVerification';

// ----------------------------------------------------------------------

const getRoleRecord = (payload) => {
  const records = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : payload
        ? [payload]
        : [];

  return records.find((record) => record?.role_definition) || null;
};

export const buildAuthenticatedUser = ({ loginDetail, roleRecord, accountId }) => ({
  userName: loginDetail.data.user_name,
  userEmail: loginDetail.data.user_email,
  account_type: loginDetail.data.account_info,
  role: loginDetail.role,
  account_id: accountId,
  access: roleRecord.role_definition,
  role_capabilities: {
    all_modules_access: roleRecord.all_modules_access === true,
    account_scope: roleRecord.account_scope || 'Permission based',
  },
  selected_role: String(loginDetail.data.selected_role ?? '').trim(),
});

export default function LoginForm({ setLoginSuccess }) {
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [rememberPassword, setRememberPassword] = useState(false);
  const [showOtpFlag, setShowOtpFlag] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [loginDetail, setLoginDetail] = useState("");
  const [loginDetail2, setLoginDetail2] = useState("");
  const [otpExpireTime, setOtpExpireTime] = useState(null);
  const [forgotPasswordClicked, setForgotPasswordClicked] = useState(false);
  const [sendForgotPasswordClicked, setSendForgotPasswordClicked] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [loadingFlag, setloadingFlag] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingOTP, setLoadingOTP] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState(null);
  const [otpSecondsRemaining, setOtpSecondsRemaining] = useState(null);

  useEffect(() => {
    if (
      !showOtpFlag
      || forgotPasswordClicked
      || verificationMethod !== LOGIN_VERIFICATION_METHODS.EMAIL
      || !Number.isFinite(otpExpireTime)
    ) {
      return undefined;
    }

    const updateSecondsRemaining = () => {
      setOtpSecondsRemaining(getEmailOtpSecondsRemaining(otpExpireTime));
    };

    updateSecondsRemaining();
    const timerId = window.setInterval(updateSecondsRemaining, 1000);

    return () => window.clearInterval(timerId);
  }, [forgotPasswordClicked, otpExpireTime, showOtpFlag, verificationMethod]);

  useEffect(() => {
    if (
      !forgotPasswordClicked
      || !sendForgotPasswordClicked
      || forgotPasswordSent
      || !Number.isFinite(otpExpireTime)
    ) {
      return undefined;
    }

    const updateSecondsRemaining = () => {
      setOtpSecondsRemaining(getEmailOtpSecondsRemaining(otpExpireTime));
    };

    updateSecondsRemaining();
    const timerId = window.setInterval(updateSecondsRemaining, 1000);

    return () => window.clearInterval(timerId);
  }, [forgotPasswordClicked, forgotPasswordSent, otpExpireTime, sendForgotPasswordClicked]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = userName.toString().trim().toLowerCase();

    if (normalizedEmail === '') {
      Swal.fire("Please enter Email")
      return;
    }
    if (password.toString().trim() === '') {
      Swal.fire("Please enter Password")
      return;
    }

    setLoadingLogin(true);

    try {
      const credentialResponse = await axios.post(baseURL + "/check_user_cred", {
        userName: normalizedEmail,
        password,
      });
      const credentialPayload = credentialResponse.data;

      if (credentialPayload?.status === 'DeActivated') {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Account deactivated',
          text: 'Your user account has been deactivated. Please contact support@caledontechnologies.com.',
        });
        return;
      }

      if (credentialPayload?.status !== 'success') {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Sign-in failed',
          text: 'The email or password is incorrect.',
        });
        return;
      }

      const accountId = credentialPayload?.data?.account_id;
      const selectedRole = credentialPayload?.data?.selected_role;
      if (!accountId || !selectedRole) {
        throw new Error('LOGIN_DETAILS_UNAVAILABLE');
      }

      const roleResponse = await axios.post(baseURL + "/get_user_role_detail", {
        user_role: selectedRole,
        account_id: accountId,
        companyNumber: String(accountId),
      });
      if (!getRoleRecord(roleResponse.data)) {
        throw new Error('ROLE_DEFINITION_UNAVAILABLE');
      }

      const challengeResponse = await axios.post(
        baseURL + "/mail_authentication_check",
        buildLoginChallengeRequest({
          email: normalizedEmail,
          accountId,
          role: selectedRole,
        }),
      );
      const challenge = parseLoginVerificationChallenge(challengeResponse.data);
      const issuedAt = challenge.verificationMethod === LOGIN_VERIFICATION_METHODS.EMAIL
        ? Date.now()
        : null;

      setOtpValue("");
      setOtpExpireTime(issuedAt);
      setOtpSecondsRemaining(challenge.expiresInSeconds);
      setVerificationMethod(challenge.verificationMethod);
      setLoginDetail2(roleResponse.data);
      setLoginDetail(credentialPayload);
      setShowOtpFlag(true);
    } catch (error) {
      const roleUnavailable = [
        'LOGIN_DETAILS_UNAVAILABLE',
        'ROLE_DEFINITION_UNAVAILABLE',
      ].includes(error?.message);
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;

      Swal.fire({
        position: 'center',
        icon: 'error',
        title: roleUnavailable ? 'Access setup unavailable' : 'Verification unavailable',
        text: roleUnavailable
          ? 'Your account was recognized, but its role permissions could not be loaded. Please contact support.'
          : (serverMessage || 'The login verification could not be started. Please try again.'),
      });
    } finally {
      setLoadingLogin(false);
    }
  }

  //Login Authentication
  const handleAuthentication = async (e) => {

    e.preventDefault();

    const authenticationKey = otpValue.toString().trim();
    const normalizedEmail = userName.toString().trim().toLowerCase();
    const accountId = loginDetail?.data?.account_id;

    if (authenticationKey === '') {
      Swal.fire("Please enter Authentication Key")
      return;
    }

    if (!accountId || !verificationMethod) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Verification unavailable',
        text: 'Your verification session is incomplete. Please return to sign in and try again.',
      });
      setShowOtpFlag(false);
      return;
    }

    if (
      verificationMethod === LOGIN_VERIFICATION_METHODS.EMAIL
      && isEmailOtpExpired(otpExpireTime)
    ) {
      Swal.fire({
        position: 'center',
        icon: 'error',
        title: 'Authentication key expired',
        text: 'The emailed authentication key is valid for 5 minutes. Please sign in again for a new key.',
      })

      setShowOtpFlag(false);
      setVerificationMethod(null);
      return;
    }

    setLoadingOTP(true);

    let authenticationMatched = false;

    try {
      const response = await axios.post(
        baseURL + "/check_authentication_key",
        buildLoginVerificationRequest({
          authenticationKey,
          email: normalizedEmail,
          accountId,
          verificationMethod,
        }),
      );
      if (!isLoginVerificationMatched(response.data, verificationMethod)) {
        Swal.fire({
          position: 'center',
          icon: 'error',
          title: 'Authentication failed',
          text: 'The authentication key is invalid or has expired.'
        });
        return;
      }

      authenticationMatched = true;

      if (!loginDetail?.data?.selected_role) {
        throw new Error('LOGIN_DETAILS_UNAVAILABLE');
      }

      let roleRecord = getRoleRecord(loginDetail2);

      if (!roleRecord) {
        const roleResponse = await axios.post(baseURL + "/get_user_role_detail", {
          user_role: loginDetail.data.selected_role,
          account_id: accountId,
          companyNumber: String(accountId),
        });
        roleRecord = getRoleRecord(roleResponse.data);
      }

      if (!roleRecord) {
        throw new Error('ROLE_DEFINITION_UNAVAILABLE');
      }

      sessionStorage.setItem('user_token', JSON.stringify(buildAuthenticatedUser({
        loginDetail,
        roleRecord,
        accountId,
      })));

      setLoginSuccess(true);
      window.location.reload();
    } catch (error) {
      const invalidAuthentication = [400, 401, 403].includes(error?.response?.status);
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;

      Swal.fire({
        position: 'center',
        icon: 'error',
        title: authenticationMatched
          ? 'Access setup unavailable'
          : invalidAuthentication
            ? 'Authentication failed'
            : 'Verification unavailable',
        text: authenticationMatched
          ? 'Your key was accepted, but your role permissions could not be loaded. Please sign in again or contact support.'
          : invalidAuthentication
            ? (serverMessage || 'The authentication key is invalid or has expired.')
            : 'The authentication service could not be reached. Please try again.'
      });
    } finally {
      setLoadingOTP(false);
    }
  }

  // send Forgot Password OTP
  const handleSendForgetPassword = (e) => {

    e.preventDefault();
    setloadingFlag(true);

    axios.post(baseURL + "/mail_forget_password_otp", { 'user_email': userName.toString().trim().toLowerCase() }).then((res) => {

      if (res.data === 'failed') {

        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'The email you entered is not registed with us'
        })
        setShowOtpFlag(false);
        setloadingFlag(false);

        return
      }
      else {
        const challenge = parsePasswordResetChallenge(res.data);
        setSendForgotPasswordClicked(true);
        setOtpExpireTime((new Date()).valueOf());
        setOtpSecondsRemaining(challenge.expiresInSeconds);
        setOtpValue('');
        setloadingFlag(false);
      }

    }).catch((e) => {
      Swal.fire({
        icon: 'error',
        title: 'Password reset unavailable',
        text: e?.response?.data?.error || 'The authentication key could not be sent. Please try again.'
      })

      setShowOtpFlag(false);
      setloadingFlag(false);
    });

  }

  // Forget Password Authentication
  const handleForgotPasswordAuthentication = (e) => {

    e.preventDefault();

    if (otpValue.toString().trim() === '') {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Please enter Authentication Key'
      })
      return;
    }

    if (isEmailOtpExpired(otpExpireTime)) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication key expired',
        text: 'The password-reset authentication key is valid for 5 minutes. Request a new key.'
      })

      setShowOtpFlag(false);
      setSendForgotPasswordClicked(false);
      setForgotPasswordClicked(false);
      return;
    }

    axios.post(baseURL + "/check_forgot_password_authentication_key",
      { 'authentication_key': otpValue, 'email': userName.toString().trim().toLowerCase() })
      .then((res) => {
        if (res.data === 'Matched') {

          setloadingFlag(true);
          // const pass = handleGeneratePassword();
          axios.post(baseURL + "/mail_new_password", { 'user_email': userName.toString() }).then((res) => {

            setForgotPasswordSent(true);
            setloadingFlag(false);
          })
            .catch((e) => {
              Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Something went wrong !!!'
              })

              setShowOtpFlag(false);
            });

        }
        else {
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: 'Authentication key not matched'
          })

          setShowOtpFlag(false);
          setSendForgotPasswordClicked(false);
          setForgotPasswordClicked(false);
          setForgotPasswordSent(false);
        }
      }).catch((e) => {
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Authentication key not matched'
        })
        setShowOtpFlag(false);
        setSendForgotPasswordClicked(false);
        setForgotPasswordClicked(false);
        setForgotPasswordSent(false);
      });
  }

  const formView = forgotPasswordSent
    ? {
      eyebrow: 'Account recovery',
      title: 'New password sent',
      subtitle: 'Your account is ready for a fresh sign-in.'
    }
    : forgotPasswordClicked && sendForgotPasswordClicked
      ? {
        eyebrow: 'Email verification',
        title: 'Check your inbox',
        subtitle: 'Enter the authentication key sent to your registered email.'
      }
      : forgotPasswordClicked
        ? {
          eyebrow: 'Account recovery',
          title: 'Reset your password',
          subtitle: 'We will send a new password to your registered email.'
        }
        : showOtpFlag
          ? verificationMethod === LOGIN_VERIFICATION_METHODS.CUSTOMIZED
            ? {
              eyebrow: 'Configured verification',
              title: 'Enter your customized OTP',
              subtitle: 'Use the customized OTP configured for this portal account.'
            }
            : {
              eyebrow: 'Two-step verification',
              title: 'Enter your emailed OTP',
              subtitle: 'Enter the one-time authentication key sent to your registered email.'
            }
          : {
            eyebrow: 'Secure access',
            title: 'Welcome back',
            subtitle: 'Sign in to continue to Manual Vendor Portal.'
          };

  return (

    <form className="mvp-auth-form" aria-labelledby="mvp-auth-title" noValidate>
      <div className="mvp-form-heading">
        <span className="mvp-form-heading__eyebrow">{formView.eyebrow}</span>
        <h2 id="mvp-auth-title">{formView.title}</h2>
        <p>{formView.subtitle}</p>
      </div>
      {

        forgotPasswordClicked && !sendForgotPasswordClicked ?
          (<>

            <Stack spacing={3} sx={{ mb: 2.5 }}>
              <div className="mvp-form-note">
                Enter the email address connected to your portal account.
              </div>
            </Stack>

            <Stack spacing={3} sx={{ mb: 2.5 }}>
              <TextField
                fullWidth
                autoComplete="email"
                type="email"
                label="Registered email"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
              />
            </Stack>

            <LoadingButton className="mvp-primary-button" fullWidth size="large" type="submit" variant="contained" loading={loadingFlag} onClick={(e) => handleSendForgetPassword(e)}>
              Send new password
            </LoadingButton>
          </>)
          :
          !sendForgotPasswordClicked && (!showOtpFlag ?
            (
              <>
                <Stack spacing={3}>


                  <TextField
                    fullWidth
                    autoComplete="username"
                    type="text"
                    label="Username or email"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                  />

                  <TextField
                    fullWidth
                    autoComplete="current-password"
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            type="button"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>

                <Stack className="mvp-auth-options" direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={rememberPassword} onChange={() => setRememberPassword(!rememberPassword)} />}
                    label="Remember me"
                  />

                  <Link className="mvp-auth-link" component="button" type="button" variant="subtitle2" underline="none" onClick={() => setForgotPasswordClicked(true)}>
                    Forgot password?
                  </Link>

                </Stack>

                <LoadingButton className="mvp-primary-button" fullWidth size="large" type="submit" variant="contained" loading={loadingLogin} onClick={handleSubmit}>
                  Sign in
                </LoadingButton>
              </>
            )
            :
              (<>

                <Stack spacing={3} sx={{ mb: 2.5 }}>
                  <div className="mvp-form-note" role="status">
                    {verificationMethod === LOGIN_VERIFICATION_METHODS.CUSTOMIZED
                      ? 'Enter the customized OTP configured for this portal account. No email expiry timer applies.'
                      : `The emailed authentication key expires in ${formatEmailOtpTimeRemaining(otpSecondsRemaining)}.`}
                  </div>
                </Stack>
                <Stack spacing={3} sx={{ mb: 2.5 }}>

                  <TextField
                    fullWidth
                    autoComplete="one-time-code"
                    type="text"
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    label={verificationMethod === LOGIN_VERIFICATION_METHODS.CUSTOMIZED
                      ? 'Customized OTP'
                      : 'Emailed authentication key'}
                    value={otpValue}
                    onChange={(e) => setOtpValue(e.target.value)}
                  />
                </Stack>

                <LoadingButton className="mvp-primary-button" fullWidth size="large" type="submit" variant="contained" loading={loadingOTP} onClick={(e) => handleAuthentication(e)}>
                  Verify and continue
                </LoadingButton>
              </>)

          )
      }

      {
        forgotPasswordClicked && sendForgotPasswordClicked && !forgotPasswordSent && (<>

          <Stack spacing={3} sx={{ mb: 2.5 }}>
            <div className="mvp-form-note" role="status">
              The password-reset authentication key expires in {formatEmailOtpTimeRemaining(otpSecondsRemaining)}.
            </div>
          </Stack>
          <Stack spacing={3} sx={{ mb: 2.5 }}>

            <TextField
              fullWidth
              autoComplete="one-time-code"
              type="text"
              inputProps={{ inputMode: 'numeric' }}
              label="Authentication key"
              value={otpValue}
              onChange={(e) => setOtpValue(e.target.value)}
            />
          </Stack>

          <LoadingButton className="mvp-primary-button" fullWidth size="large" type="submit" variant="contained" loading={loadingFlag} onClick={(e) => handleForgotPasswordAuthentication(e)}>
            Verify email
          </LoadingButton>
        </>)

      }

      {
        forgotPasswordSent &&
        <>
          <Stack spacing={3} sx={{ mb: 2.5 }}>
            <div className="mvp-form-success" role="status">
              <CheckCircleIcon color="success" />
              A new password was sent to your registered email. Use it to sign in.
            </div>
          </Stack>

          <LoadingButton className="mvp-primary-button" fullWidth size="large" type="submit" variant="contained" loading={false} onClick={() => {
            setShowOtpFlag(false);
            setSendForgotPasswordClicked(false);
            setForgotPasswordClicked(false);
            setForgotPasswordSent(false);
          }}>
            Return to sign in
          </LoadingButton>
        </>
      }


    </form>

  );
}
